import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deletePage, getPageDetails, hidePage, movePage, upsertDraftPage, upsertPublishedPage } from '@/lib/api/pages';
import { stanNotify } from '@/lib';
import { PRODUCT_TYPES, QUERY_KEYS } from '@/lib/constants';
import { ServerPageData } from '@/features/product-editor/types';

export function useDeletePage(storeId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (pageId: number) => deletePage(storeId, pageId),
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Product Deleted',
                text: 'Product has been deleted successfully',
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Delete Failed',
                text: error.response?.data?.message || 'Failed to delete product',
            });
        },
    });
}

export function useHidePage(storeId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (pageId: number) => hidePage(storeId, pageId),
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Page Hidden',
                text: 'Page has been unpublished successfully',
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Unpublish Failed',
                text: error.response?.data?.message || 'Failed to unpublish page',
            });
        },
    });
}

export function useMovePage(storeId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ pageId, moveTo }: { pageId: number; moveTo: 'landing' | 'store' }) =>
            movePage(storeId, pageId, moveTo),
        onSuccess: (_data, variables) => {
            stanNotify({
                type: 'success',
                title: 'Page Moved',
                text: `Page moved to ${variables.moveTo === 'landing' ? 'Landing Pages' : 'Store'} successfully`,
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Move Failed',
                text: error.response?.data?.message || 'Failed to move page',
            });
        },
    });
}

export function useDuplicatePage(storeId: number) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (pageId: number) => {
            const pageOriginal: ServerPageData = await getPageDetails(storeId, pageId);

            delete pageOriginal.product.id;

            // TODO fix lowercase check
            if (pageOriginal.product.type === PRODUCT_TYPES.COURSE) {
                pageOriginal.product.course.modules.forEach((module: any) => {
                    delete module.module_id;
                    module.lessons.forEach((lesson: any) => {
                        delete lesson.lesson_id;
                    });
                });
            }

            const product = { ...pageOriginal.product };

            if (product.price) {
                if (product.price?.membership_id) {
                    delete product.price.membership_id;
                }
                if (product.price?.price_options) {
                    Object.keys(product.price.price_options).forEach(key => {
                        if (product.price.price_options && product.price.price_options[key]?.membership_id) {
                            delete product.price.price_options[key].membership_id;
                        }
                    });
                }
            }

            if (product.webinar?.sessions) {
                product.webinar.sessions = [];
            }

            product.title = `${product.title} (Copy)`;

            // TODO: Verify
            const button = pageOriginal.button;
            if (button.layout === 'button') {
                button.button_text = `${button.button_text} (Copy)`;
            } else {
                button.tagline = `${button.tagline} (Copy)`;
            }

            const pagePayload: Partial<ServerPageData> = {
                top_banner_text: pageOriginal.top_banner_text || null,
                product,
                button,
                order: 999,
                status: 1,
                location: pageOriginal.location,
            };

            return await upsertDraftPage(storeId, pagePayload);
        },
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Product Duplicated',
                text: 'Product has been duplicated successfully',
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Duplication Failed',
                text: error.response?.data?.message || 'Failed to duplicate product',
            });
        },
    });
}

export function useUpsertDraftPage(storeId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (pageData: ServerPageData) => upsertDraftPage(storeId, pageData),
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Draft Saved',
                text: 'Draft has been saved successfully',
                duration: 3000,
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Draft Save Failed',
                text: error.response?.data?.message || 'Failed to save draft',
            });
        },
    });
}
export function useUpsertPage(storeId: number) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (pageData: ServerPageData) => upsertPublishedPage(storeId, pageData),
        onSuccess: () => {
            stanNotify({
                type: 'success',
                title: 'Page Updated',
                text: 'Page has been updated successfully',
                duration: 3000,
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.STORE_DATA] });
        },
        onError: (error: any) => {
            stanNotify({
                type: 'error',
                title: 'Update Failed',
                text: error.response?.data?.message || 'Failed to update page',
            });
        },
    });
}
